import { createAction } from '@reduxjs/toolkit';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction, Dispatch } from 'redux';
import type { AccommodationState, BookingCustomer, BookingProduct, ThunkResult } from '@/interfaces';
import {
    visBookInitiatePayment,
    visBookMakeReservation,
    visBookLoginByEmail,
    visBookLoginBySms,
    visBookPingReservation,
    visBookVerifyToken,
} from '@/utils/fetch/visbook/visBookFetcher';
import type { VisBookReservation } from '@/server/integration/visbook/visbookResponseTypes';
import { extractVisBookBookingPageParams } from '@/server/integration/visbook/visbookResponseMapper';

export const setCustomer = createAction<BookingCustomer>('BOOKING/SET_CUSTOMER');
export const createProduct = createAction<BookingProduct>('BOOKING/CREATE_PRODUCT');
export const paymentError = createAction<number>('BOOKING/PAYMENT_ERROR');
export const setReservation = createAction('BOOKING/SET_RESERVATION');
export const setReservationSuccess = createAction<VisBookReservation>('BOOKING/SET_RESERVATION_SUCCESS');
export const setReservationFailure = createAction('BOOKING/SET_RESERVATION_FAILURE');
export const startVerification = createAction<string>('BOOKING/START_VERIFICATION');
export const startVerificationSuccess = createAction<string>('BOOKING/START_VERIFICATION_SUCCESS');
export const startVerificationFailure = createAction<string>('BOOKING/START_VERIFICATION_FAILURE');
export const createVerification = createAction<string>('BOOKING/CREATE_VERIFICATION');
export const createVerificationSuccess = createAction<string>('BOOKING/CREATE_VERIFICATION_SUCCESS');
export const createVerificationFailure = createAction<string>('BOOKING/CREATE_VERIFICATION_FAILURE');
export const createVerificationReset = createAction<string>('BOOKING/CREATE_VERIFICATION_RESET');

export const makeVisBookReservation =
    (): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        dispatch(setReservation());
        let status: number;
        let data: VisBookReservation;

        try {
            const bookingParams = extractVisBookBookingPageParams(getState().booking.product.bookingParams);
            const reservationData = await visBookMakeReservation(
                getState().hotelPage.id,
                bookingParams.productId,
                bookingParams.priceId,
                getState().filters.checkInDate,
                getState().filters.checkOutDate,
                getState().filters.rooms,
            );

            status = reservationData.status;
            data = reservationData.data;
        } catch (e) {
            status = 500;
        }

        if (status === 200) {
            dispatch(setReservationSuccess(data));
        } else {
            dispatch(setReservationFailure());
        }
    };

export const pingVisBookReservation =
    (): ThunkResult<Promise<void>> =>
    async (dispatch: ThunkDispatch<AccommodationState, void, AnyAction>, getState: () => AccommodationState): Promise<void> => {
        const reservationId = getState().booking.reservation.reservationId;
        const { status } = await visBookPingReservation(
            getState().hotelPage.id,
            reservationId,
            getState().booking.reservation.encryptedCompanyId,
        );

        if (status === 200) {
            return;
        }
        if (status === 202) {
            // 202 = reservation has expired
            // Should perhaps do something smarter than just automatically creating a new reservation?
            await dispatch(makeVisBookReservation());
        } else {
            dispatch(setReservationFailure());
        }
    };

export const submitDetailsAndGoToVisBookPaymentTerminal =
    (): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        const auth = sessionStorage.getItem('vb-auth');
        const state = getState();
        const { status, data } = await visBookInitiatePayment(
            state.filters,
            state.hotelPage.id,
            state.booking.reservation as VisBookReservation,
            state.booking.customer,
            state.booking.product.paymentSettings.type,
            state.booking.product.totalPrice,
            state.booking.status.verification,
            auth,
            state.booking.product.bookingParams,
        );
        if (status >= 200 && status < 300 && data.terminalUrl) {
            window.location = data.terminalUrl;
        } else {
            throw new Error('Failed to confirm reservation');
        }
    };

export const registerVisBookAccountByEmail =
    (email: string): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        dispatch(setCustomer({ email }));

        const { status } = await visBookLoginByEmail(getState().hotelPage.id, email);
        if (status >= 200 && status < 300) {
            dispatch(createVerification('email'));
            return Promise.resolve();
        }
        dispatch(createVerificationFailure('email'));
        return Promise.reject();
    };

export const registerVisBookAccountByPhone =
    (phoneCountryCode: string, phone: string): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        dispatch(setCustomer({ phone: `+${phoneCountryCode} ${phone}` }));

        const { status } = await visBookLoginBySms(getState().hotelPage.id, phoneCountryCode, phone);
        if (status >= 200 && status < 300) {
            dispatch(createVerification('phone'));
            return Promise.resolve();
        }
        dispatch(createVerificationFailure('phone'));
        return Promise.reject();
    };

export const verifyVisBookAccount =
    (type: 'email' | 'phone', code: string): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        const { status, data } = await visBookVerifyToken(getState().hotelPage.id, type, code);
        if (status >= 200 && status < 300) {
            const authentication = data.authentication;
            const { firstName, lastName, address, zipCode, city: area, company, country } = data.customer || {};
            const email = getState().booking.customer?.email || data.customer?.email || '';
            const customer = {
                ...getState().booking.customer,
                firstName: firstName || '',
                lastName: lastName || '',
                email: email || '',
                address: address || '',
                zipCode: zipCode || '',
                area: area || '',
                company: company || '',
                country: country?.toString() || '',
            };

            sessionStorage.setItem('vb-auth', authentication);

            const parsePhone = (phone: string): [string, string] => {
                const phoneMatch = phone.match(/\+([0-9-]{1,4})\s([0-9 -]{4,})|(^[^+][0-9 -]{4,})/m);
                if (phoneMatch[1]) {
                    return [phoneMatch[1], phoneMatch[2]];
                }
                if (phoneMatch[3]) {
                    return ['47', phoneMatch[3]];
                }
                return ['47', phone];
            };

            if (type === 'email') {
                const [phoneCountryCode, phone] = data.customer?.phone ? parsePhone(data.customer.phone) : ['', ''];
                customer.phone = phone;
                customer.phoneCountryCode = phoneCountryCode;
            }
            dispatch(setCustomer(customer));
            dispatch(createVerificationSuccess(type));
        } else {
            throw new Error('Unauthorized');
        }
    };
